import React from 'react';
import { Dictionaries } from 'src/pages/app/_BLL/metaDictionaries/types';
import { BooleanDropDownField } from 'src/pages/CompanyPage/ui/dataPointGenerator/fields/BooleanDropDownField/BooleanDropDownField';
import { BooleanField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/BooleanField/BooleanField';
import { DateField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/DateField/DateField';
import { DeadlineField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/DeadlineField/DeadlineField';
import { DropDownField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/DropDownField/DropDownField';
import { DropDownManyField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/DropDownManyField/DropDownManyField';
import { IntegerFloatField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/IntegerFloatField/IntegerFloatField';
import { PercentageField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/PercentageField/PercentageField';
import { StringField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/StringField/StringField';
import { StringManyField } from 'src/pages/CompanyPage/ui//dataPointGenerator/fields/StringManyField/StringManyField';
import { DataPointMeta, FieldDictionary, PropertyHandlingType, ValidateFunction } from '../../_BLL/types';

interface Props {
	namePrefix: string;
	handlingType: PropertyHandlingType;
	propertyName: string;
	datapointMeta: DataPointMeta;
	dictionaries: Dictionaries;
}

export const DataPointGenerator: React.FC<Props> = (props) => {
	const {
		namePrefix,
		handlingType,
		propertyName,
		datapointMeta: {
			internalName,
			fieldType,
			isEditable, // ? There are no uneditable fields.
			allowsNull,
			allowsMany,
			measureUnits,
			rangeLow,
			rangeHigh,
			dictionary,
			options,
		},
		dictionaries,
	} = props;

	const fieldDictionary = dictionaries && dictionary && (dictionaries[dictionary] as FieldDictionary);

	const validate: ValidateFunction = (value: any) => {
		let error;

		if (typeof value === 'string' && value.length > 400) {
			error = 'Value must be less then 400 characters';
		}

		if (rangeLow && rangeHigh === undefined) {
			if (+value < rangeLow && value !== null) {
				error = `Value must be higher then ${rangeLow}`;
			}
		}

		if (rangeHigh && rangeLow === undefined) {
			if (+value > rangeHigh && value !== null) {
				error = `Value must be smaller then ${rangeHigh}`;
			}
		}

		if (rangeLow && rangeHigh) {
			if ((+value < rangeLow || +value > rangeHigh) && value !== null) {
				error = `Value must be between ${rangeLow} and ${rangeHigh}`;
			}
		}

		if (!allowsNull && value === null) {
			error = 'Field cannot be empty';
		}

		return error;
	};

	return (
		<>
			{internalName && (
				<>
					{(fieldType === 'Id' ||
						fieldType === 'Integer' ||
						fieldType === 'Currency' ||
						fieldType === 'Float' ||
						fieldType === 'FloatDetailed' ||
						fieldType === 'YearAge' ||
						fieldType === 'AgeFloat') && (
						<IntegerFloatField
							namePrefix={namePrefix}
							propertyName={propertyName}
							internalName={internalName}
							validate={validate}
							measureUnits={measureUnits}
							valueType={fieldType}
						/>
					)}

					{fieldType === 'String' && (
						<>
							{allowsMany ? (
								<StringManyField
									namePrefix={namePrefix}
									propertyName={propertyName}
									internalName={internalName}
									validate={validate}
									measureUnits={measureUnits}
								/>
							) : (
								<StringField
									namePrefix={namePrefix}
									propertyName={propertyName}
									internalName={internalName}
									validate={validate}
									measureUnits={measureUnits}
								/>
							)}
						</>
					)}

					{fieldType === 'Boolean' && (
						<BooleanField
							namePrefix={namePrefix}
							propertyName={propertyName}
							internalName={internalName}
							validate={validate}
							options={options}
						/>
					)}

					{fieldType === 'BooleanDropDown' && (
						<BooleanDropDownField
							namePrefix={namePrefix}
							propertyName={propertyName}
							internalName={internalName}
							validate={validate}
							options={options}
						/>
					)}

					{fieldType === 'Percentage' && (
						<PercentageField
							namePrefix={namePrefix}
							propertyName={propertyName}
							internalName={internalName}
							validate={validate}
							measureUnits={measureUnits}
							handlingType={handlingType}
						/>
					)}

					{fieldType === 'DropDown' && (
						<>
							{allowsMany ? (
								<DropDownManyField
									namePrefix={namePrefix}
									propertyName={propertyName}
									internalName={internalName}
									validate={validate}
									fieldDictionary={fieldDictionary}
								/>
							) : (
								<DropDownField
									namePrefix={namePrefix}
									propertyName={propertyName}
									internalName={internalName}
									validate={validate}
									fieldDictionary={fieldDictionary}
								/>
							)}
						</>
					)}

					{fieldType === 'Date' && (
						<>
							{internalName === 'deadline' ? (
								<DeadlineField
									namePrefix={namePrefix}
									propertyName={propertyName}
									internalName={internalName}
								/>
							) : (
								<DateField
									namePrefix={namePrefix}
									propertyName={propertyName}
									internalName={internalName}
									measureUnits={measureUnits}
								/>
							)}
						</>
					)}
				</>
			)}
		</>
	);
};
